/**
 * Form Access Service
 *
 * Business logic layer for form access submission.
 * Handles error mapping and response normalization.
 */

import { FormAccessRequestData, FormAccessResponseData } from '../types/api.types';
import { submitFormAccess } from './formAccessApi';

/**
 * Result structure returned to UI after form submission.
 * Includes either success data or error information.
 */
export interface FormAccessResult {
  success: boolean;
  formAccessId?: string;
  redirectUrl?: string;
  downloaded?: boolean;   // true when backend streamed PDF directly
  fileName?: string;
  error?: {
    code: string;
    message: string;
    userMessage: string;
  };
}

/**
 * Submit form access request with error handling and mapping.
 *
 * @param request - Form data from Step 3
 * @param sessionToken - Bearer token from Step 2
 * @returns FormAccessResult with success or error details
 */
export async function postFormAccess(
  request: FormAccessRequestData,
  sessionToken: string,
  formAccessId?: string | null
): Promise<FormAccessResult> {
  try {
    const response = await submitFormAccess(request, sessionToken, formAccessId);

    return {
      success:     true,
      formAccessId: response.formAccessId,
      redirectUrl:  response.redirectUrl,
      downloaded:   response.downloaded,
      fileName:     response.fileName
    };
  } catch (err: any) {
    const data = err.data as FormAccessResponseData;

    const errorCode = data?.error || 'UNKNOWN_ERROR';
    const errorMessageMap: Record<string, string> = {
      'IDENTITY_MISMATCH'     : 'We’re unable to retrieve your form. Please verify your information and try again.\nIf you continue to experience issues, please contact your HR department for assistance.',
      'IDENTITY_MISMATCH_DUP' : 'We’re unable to retrieve your form. Please contact your HR department for assistance. ErrorCode : DUP.',
      'SESSION_EXPIRED'       : 'Your session has expired. Please start over from Step 2.',
      'MISSING_FIELDS'        : 'Required fields are missing. Please fill in all required fields.',
      'UNAUTHORIZED'          : 'Authorization failed. Please start over.',
      /* 'NO_FORMS_AVAILABLE': 'No forms are available for the provided input. Please verify your information and try again.', */
       'NO_FORMS_AVAILABLE'   : 'We’re unable to retrieve your form at this time.Please verify your information and try again. \n If you believe your form should be available, please contact your HR department for assistance.',
      'UNKNOWN_ERROR'         : 'An unexpected error occurred. Please try again later.'
    };

    return {
      success: false,
      error: {
        code: errorCode,
        message: data?.message || 'Request failed',
        userMessage: errorMessageMap[errorCode] || errorMessageMap['UNKNOWN_ERROR']
      }
    };
  }
}
